import ResizeObserver from 'resize-observer-polyfill';
import {AttrsSchema} from 'panel';

import {StackableComponent, VisibilityChangeEvent} from 'common/components/base/stackable';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {CaretPos} from './types';
import {template} from './template';
import css from './index.styl?inline';

export enum Directions {
  Up = `up`,
  Down = `down`,
}
export interface DropMenuAttrs {
  open?: boolean;
  direction?: Directions;
  responsive?: boolean;
  'with-caret'?: boolean;
  'caret-pos'?: CaretPos;
  'caret-pos-left'?: string;
  'use-fixed-pos'?: boolean;
}

export interface DropMenuProps {
  attrs?: DropMenuAttrs;
  on?: {
    change?: (ev: VisibilityChangeEvent) => void;
  };
}

const VERTICAL_OFFSET = 4;

export class DropMenu extends StackableComponent<EmptyObj, DropMenuAttrs> {
  static get attrsSchema(): AttrsSchema<DropMenuAttrs> {
    return {
      ...super.attrsSchema,
      open: {
        description: `Is the dropdown menu open?`,
        type: `boolean`,
      },
      direction: {
        description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(Directions),
        default: Directions.Down,
      },
      'with-caret': {
        description: `Show a little caret pointer?`,
        type: `boolean`,
      },
      'caret-pos': {
        description: `position of caret, if caret is shown`,
        type: `json`,
        tsType: `CaretPos`,
      },
      'caret-pos-left': {
        description: `DEPRECATED: position of caret from left. use 'caret-pos' as it supports both left and right`,
        type: `string`,
        default: `50%`,
      },
      // TODO @junhouse
      // When a dropmenu is rendered within a container, we need a mechanism to allow the dropmenu to overflow its container.
      // Using fixed position, we temporarily achieve this behavior, mainly in sharing modal. This is an interim solution,
      // while we find a generic solution. When using this attribute with scroll, one needs to make sure to close the dropmenu
      // on scroll event.
      'use-fixed-pos': {
        description: `Use fixed position instead of absolute`,
        type: `boolean`,
      },
      responsive: {
        description: `align menu based on parent and screen space`,
        type: `boolean`,
      },
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      helpers: {
        onBodyPatch: () => {
          this.updateResponsivePosition(true);
        },
        isResponsive: () => {
          return !this.attr(`use-fixed-pos`) && this.attr(`responsive`);
        },
      },
    };
  }

  private resizeObserver: ResizeObserver;
  private isAlignedToTop: boolean = null;
  private _boundaryEl: HTMLElement;

  get trapTab() {
    return false;
  }

  private renderWithFixedPosition() {
    const rect = this.offsetParent.getBoundingClientRect();
    this.style.position = `fixed`;
    this.style.top = `${rect.bottom + VERTICAL_OFFSET}px`;
    this.style.left = `${rect.x}px`;
    this.style.right = ``;
    this.style.bottom = ``;
  }

  private renderWithResponsivePosition() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateResponsivePosition(false);
    });

    this.resizeObserver.observe(this);
    this.updateResponsivePosition(true);
  }

  /**
   * Calculate and assign positioning styles to the menu.
   * @param updateAlignment - we don't want to update the alignment when resizing,
   * as it might be jarring for the menu to flip positioning while interacting with it
   */
  private updateResponsivePosition(updateAlignment: boolean) {
    // the menu anchor should always be the closest positioned parent
    const anchorRect = this.offsetParent.getBoundingClientRect();
    const menuRect = this.getBoundingClientRect();
    const styles: Partial<CSSStyleDeclaration> = {
      right: ``,
      bottom: ``,
      top: `${anchorRect.height + VERTICAL_OFFSET}px`,
    };

    const boundingElementRect = this._boundaryEl?.getBoundingClientRect();
    const boundingWidth = boundingElementRect
      ? boundingElementRect.left + boundingElementRect.width
      : window.innerWidth;
    if (anchorRect.left + menuRect.width >= boundingWidth) {
      styles.left = `${this.offsetParent.clientWidth - menuRect.width}px`;
    }

    // bottomBound and topBound are relative to viewport
    const bottomBound = boundingElementRect?.bottom ?? window.innerHeight;
    const topBound = boundingElementRect?.top ?? 0;
    const hasRoomBelow = anchorRect.bottom + menuRect.height + VERTICAL_OFFSET < bottomBound;
    const hasRoomAbove = anchorRect.top - menuRect.height - VERTICAL_OFFSET > topBound;

    // only align to top if there's room
    const shouldAlignToTop = !hasRoomBelow && hasRoomAbove;
    if (shouldAlignToTop || (!updateAlignment && this.isAlignedToTop)) {
      styles.top = `${-menuRect.height - VERTICAL_OFFSET}px`;
    }

    if (updateAlignment) {
      this.isAlignedToTop = shouldAlignToTop;
    }

    Object.assign(this.style, styles);
  }

  set boundaryEl(el: HTMLElement) {
    this._boundaryEl = el;
  }

  open() {
    if (this.attr(`use-fixed-pos`)) {
      this.renderWithFixedPosition();
    } else if (this.attr(`responsive`)) {
      this.renderWithResponsivePosition();
    }

    super.open();
  }

  close() {
    this.resizeObserver?.disconnect();
    super.close();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get caretPos() {
    return this.attr(`caret-pos`) || {left: this.attr(`caret-pos-left`)};
  }
}

export const DropMenuEl = defineAndMakeJsxFactory<DropMenuProps>(`mp-drop-menu`, DropMenu);
